import React, { Suspense, useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

/**
 * NextPageWrapper:
 * 1. Handles async components (Next.js RSC pattern)
 * 2. Injects metadata into Helmet
 * 3. Passes params (slug, etc.) to the component
 */
export const NextPageWrapper = ({ modulePromise }: { modulePromise: Promise<any> }) => {
    const [Module, setModule] = useState<any>(null);
    const [Content, setContent] = useState<React.ReactNode | null>(null);
    const [metadata, setMetadata] = useState<any>(null);
    const params = useParams();
    const location = useLocation();

    useEffect(() => {
        let isMounted = true;

        modulePromise.then(async (mod) => {
            if (!isMounted) return;
            setModule(mod);

            const Page = mod.default;

            // 1. Handle Metadata
            let meta = mod.metadata || {};
            if (mod.generateMetadata) {
                try {
                    // Wrapped params to match Next.js 15+ (Promise-based)
                    const resolvedParams = Promise.resolve(params);
                    meta = await mod.generateMetadata({ params: resolvedParams });
                } catch (e) {
                    console.error("Error generating metadata:", e);
                }
            }
            setMetadata(meta);

            // 2. Handle Async/Sync Component
            // We pass params as a Promise to match Next.js 15+ patterns
            const resolvedParams = Promise.resolve(params);
            const searchParams = new URLSearchParams(location.search);

            try {
                if (typeof Page === 'function') {
                    // Check if it's an async function (RSC style)
                    if (Page.constructor.name === 'AsyncFunction') {
                        const result = await Page({ params: resolvedParams, searchParams });
                        setContent(result);
                    } else {
                        // Regular sync component
                        setContent(<Page params={resolvedParams} searchParams={searchParams} />);
                    }
                }
            } catch (e) {
                console.error("Error rendering page component:", e);
                setContent(<div className="error-page">Error loading page content: {String(e)}</div>);
            }
        });

        return () => { isMounted = false; };
    }, [modulePromise, params, location]);

    if (!Content && !metadata) {
        return <div style={{ minHeight: '80vh' }} />;
    }

    return (
        <>
            {metadata && (
                <Helmet>
                    <title>{metadata.title || "RotanaBiz"}</title>
                    {metadata.description && <meta name="description" content={metadata.description} />}
                    {metadata.keywords && <meta name="keywords" content={metadata.keywords.toString()} />}
                    {metadata.alternates?.canonical && <link rel="canonical" href={metadata.alternates.canonical} />}
                    {/* Add more meta tags as needed */}
                </Helmet>
            )}
            {Content}
        </>
    );
};
